import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react';
import axiosInstance from 'api/axiosInstance';
import apiUrls from 'api/apiUrls';

const DeviceContext = createContext();

export const DeviceProvider = ({ children }) => {
  const [deviceData, setDeviceData] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalDevices, setTotalDevices] = useState(0);
  const latestSearchRef = useRef('');
  const fetchInProgressRef = useRef(false);
  const countsFetchedRef = useRef(false);
  const pageCacheRef = useRef(new Map());
  const lastSearchTermRef = useRef('');
  const lastSortOrderRef = useRef('DESC');
  const backendPageMapRef = useRef({});
  const [totalActiveDevices, setTotalActiveDevices] = useState(0);
  const [totalInactiveDevices, setTotalInactiveDevices] = useState(0);

  const fetchDeviceAttributes = useCallback(async (deviceName) => {
    const keys =
      'warranty_date,location,app_version,device_type,patch_v,group_id,history,modbus_config_name,patch_name,config_status,patch_status,modbus_v';

    try {
      const resp = await axiosInstance.get(
        apiUrls.factorytoolgroupAttributes(deviceName, keys),
      );

      const attrs = resp.data;
      const attrMap = Object.fromEntries(attrs.map((a) => [a.key, a.value]));

      console.log(`[DeviceContext] fetchDeviceAttributes("${deviceName}") group_id:`, attrMap.group_id ?? 'null/missing');
      if (attrMap.group_id) {
        localStorage.setItem('groupID', attrMap.group_id);
        console.log(`[DeviceContext] groupID set in localStorage from "${deviceName}":`, attrMap.group_id);
      }

      return {
        warranty: attrMap.warranty_date ?? null,
        location: attrMap.location ?? '',
        app_version: attrMap.app_version ?? '-',
        device_type: attrMap.device_type ?? 0,
        patch_v: attrMap.patch_v ?? '-',
        group_id: attrMap.group_id ?? null,
        history: attrMap.history ?? [],
        modbus_config_name: attrMap.modbus_config_name ?? '-',
        patch_name: attrMap.patch_name ?? '-',
        config_status: attrMap.config_status ?? '-',
        patch_status: attrMap.patch_status ?? '-',
      };
    } catch (error) {
      if (error?.response?.status === 404) {
        return null;
      }

      console.error('Error fetching attributes:', error);
      return null;
    }
  }, []);

  const fetchActiveInactiveCounts = useCallback(async () => {
    if (countsFetchedRef.current) return;
    countsFetchedRef.current = true;

    let page = 0;
    let allDevices = [];
    let hasNextPage = true;

    while (hasNextPage) {
      const apiUrl = apiUrls.allDevices('', page);
      try {
        const resp = await axiosInstance.get(apiUrl);
        const devices = resp.data.data;
        allDevices = allDevices.concat(devices);
        hasNextPage = resp.data.hasNext;
        page += 1;
      } catch (error) {
        console.error('[DeviceContext] Error fetching page', page, error);
        break;
      }
    }

    const attrResults = await Promise.all(
      allDevices.map(async (d) => {
        const attrs = await fetchDeviceAttributes(d.name);
        return attrs ? d : null;
      }),
    );

    const validDevices = attrResults.filter(Boolean);
    const activeCount = validDevices.filter((d) => d.active).length;
    const inactiveCount = validDevices.length - activeCount;

    localStorage.setItem('totalActiveDevices', activeCount);
    localStorage.setItem('totalInactiveDevices', inactiveCount);
    localStorage.setItem('totalAllDevices', validDevices.length);

    setTotalActiveDevices(activeCount);
    setTotalInactiveDevices(inactiveCount);
  }, [fetchDeviceAttributes]);

  const fetchDeviceAssetId = async (deviceId) => {
    try {
      const resp = await axiosInstance.get(apiUrls.getRelationInfo(deviceId));

      const assetRelation = resp.data.find(
        (r) => r.from?.entityType === 'ASSET',
      );

      return assetRelation?.from?.id || null;
    } catch (error) {
      console.error('Error fetching device asset relation:', error);
      return null;
    }
  };

  const fetchAssetAdditionalInfo = async (assetId) => {
    try {
      const resp = await axiosInstance.get(apiUrls.getAdditionalInfo(assetId));
      return resp.data?.additionalInfo || {};
    } catch (error) {
      console.error('Error fetching asset info:', error);
      return {};
    }
  };

  const fetchAllDevices = useCallback(
    async (serialNumber = '', page = 0, pageSize = 10, sortOrder = 'DESC') => {
      latestSearchRef.current = serialNumber;

      const pageNumber = typeof page === 'number' && page >= 0 ? page : 0;
      const fetchKey = `${serialNumber}_${pageNumber}_${sortOrder}`;

      if (lastSearchTermRef.current !== serialNumber || lastSortOrderRef.current !== sortOrder) {
        pageCacheRef.current.clear();
        backendPageMapRef.current = {};
        lastSearchTermRef.current = serialNumber;
        lastSortOrderRef.current = sortOrder;
      }

      const cached = pageCacheRef.current.get(fetchKey);
      if (cached) {
        setDeviceData(cached.devices);
        setHasNext(cached.hasNext);
        setTotalPages(cached.totalPages);
        setTotalDevices(cached.totalDevices);
        setLoadingDevices(false);
        return;
      }

      if (fetchInProgressRef.current === fetchKey) {
        return;
      }
      fetchInProgressRef.current = fetchKey;

      setLoadingDevices(true);

      try {
        if (pageNumber === 0 && !serialNumber) {
          fetchActiveInactiveCounts();
        }

        let validDevices = [];
        let backendPage =
          backendPageMapRef.current[pageNumber] ??
          (pageNumber === 0 ? 0 : backendPageMapRef.current[pageNumber - 1] || 0);
        let hasNextPage = false;
        let totalElements = 0;
        let totalPagesCount = 0;

        while (validDevices.length < pageSize) {
          let resp;
          try {
            const apiUrl = apiUrls.allDevices(serialNumber, backendPage, sortOrder);
            resp = await axiosInstance.get(apiUrl);
          } catch (apiError) {
            break;
          }

          const devices = resp.data.data || [];
          hasNextPage = resp.data.hasNext;
          totalElements = resp.data.totalElements ?? totalElements;
          totalPagesCount = resp.data.totalPages ?? totalPagesCount;

          if (backendPageMapRef.current[pageNumber] === undefined) {
            backendPageMapRef.current[pageNumber] = backendPage;
          }

          if (devices.length === 0) break;

          console.log(`[DeviceContext] Backend page ${backendPage}: ${devices.length} devices fetched`, devices.map(d => d.name));

          const phase1 = await Promise.all(
            devices.map(async (d) => {
              const attrs = await fetchDeviceAttributes(d.name);
              if (!attrs) {
                console.log(`[DeviceContext] FILTERED OUT (no attrs): ${d.name}`);
                return null;
              }

              return {
                deviceId: d?.id?.id,
                machineModel: d.label || '',
                serialNumber: d.name,
                name: d.name,
                active: d.active ?? false,
                ...attrs,
                country: '-',
                state: '-',
                noc: '-',
                plant: '-',
                history: attrs.history ?? [],
                modbus_config_name: attrs.modbus_config_name ?? '-',
                patch_name: attrs.patch_name ?? '-',
              };
            }),
          );

          const validThisPage = phase1.filter(Boolean);
          validDevices = validDevices.concat(validThisPage);

          if (!hasNextPage || validDevices.length >= pageSize) break;
          backendPage += 1;
        }

        backendPageMapRef.current[pageNumber + 1] = backendPage + 1;

        const initialData = validDevices.slice(0, pageSize);
        console.log(`[DeviceContext] Final valid devices for UI page ${pageNumber}: ${initialData.length}`, initialData.map(d => d.serialNumber));

        const paginationMeta = {
          hasNext: hasNextPage,
          totalPages: totalPagesCount,
          totalDevices: totalElements,
        };

        if (latestSearchRef.current !== serialNumber) return;

        setDeviceData(initialData);
        setHasNext(paginationMeta.hasNext);
        setTotalPages(paginationMeta.totalPages);
        setTotalDevices(paginationMeta.totalDevices);
        setLoadingDevices(false);
        fetchInProgressRef.current = null;

        pageCacheRef.current.set(fetchKey, {
          devices: initialData,
          ...paginationMeta,
        });

        const searchSnapshot = serialNumber;
        initialData.forEach(async (device) => {
          try {
            const assetId = await fetchDeviceAssetId(device.deviceId);
            if (!assetId) return;

            const assetInfo = await fetchAssetAdditionalInfo(assetId);
            if (!assetInfo) return;

            if (latestSearchRef.current !== searchSnapshot) return;

            const assetFields = {
              country: assetInfo?.CountryName || '-',
              state: assetInfo?.StateName || '-',
              noc: assetInfo?.NocName || '-',
              plant: assetInfo?.DisplayName || '-',
            };

            setDeviceData((prev) =>
              prev.map((d) =>
                d.deviceId === device.deviceId ? { ...d, ...assetFields } : d,
              ),
            );

            const cachedEntry = pageCacheRef.current.get(fetchKey);
            if (cachedEntry) {
              cachedEntry.devices = cachedEntry.devices.map((d) =>
                d.deviceId === device.deviceId ? { ...d, ...assetFields } : d,
              );
            }
          } catch (error) {
            console.error(
              '[DeviceContext] Error fetching asset info for',
              device.serialNumber,
              error,
            );
          }
        });
      } catch (error) {
        console.error('Error fetching devices:', error);
        setLoadingDevices(false);
        fetchInProgressRef.current = null;
      }
    },
    [fetchDeviceAttributes, fetchActiveInactiveCounts],
  );

  const resetDeviceState = useCallback(() => {
    setDeviceData([]);
    setLoadingDevices(false);
    setHasNext(false);
    setTotalPages(0);
    setTotalDevices(0);
    setTotalActiveDevices(0);
    setTotalInactiveDevices(0);
    latestSearchRef.current = '';
    fetchInProgressRef.current = false;
    countsFetchedRef.current = false;
    pageCacheRef.current.clear();
    lastSearchTermRef.current = '';
    backendPageMapRef.current = {};
  }, []);

  return (
    <DeviceContext.Provider
      value={{
        deviceData,
        loadingDevices,
        hasNext,
        totalPages,
        totalDevices,
        fetchAllDevices,
        fetchDeviceAttributes,
        fetchDeviceAssetId,
        fetchAssetAdditionalInfo,
        setDeviceData,
        totalActiveDevices,
        totalInactiveDevices,
        resetDeviceState,
      }}
    >
      {children}
    </DeviceContext.Provider>
  );
};

export const useDevices = () => useContext(DeviceContext);
